
import React from "react";

/* ============================================
   REUSABLE ACTION BUTTON
============================================ */

const variants = {
  primary: `
    border-brand-navy
    bg-brand-navy
    text-white

    hover:border-brand-navyLight
    hover:bg-brand-navyLight

    dark:border-white/20
    dark:bg-white/10
    dark:text-white
    dark:backdrop-blur-xl

    dark:hover:border-white/30
    dark:hover:bg-white/15
  `,

  glass: `
    border-skin-border
    bg-white/50
    text-skin-heading
    backdrop-blur-xl

    hover:border-skin-accent/30
    hover:bg-white/80

    dark:border-white/15
    dark:bg-white/[0.07]
    dark:text-white

    dark:hover:border-white/25
    dark:hover:bg-white/[0.12]
  `,
};

function ActionButton({
  text,
  icon,
  disabled = false,
  onClick,
  variant = "primary",
  className = "",
  type = "button",
}) {
  const hasAction = typeof onClick === "function";

  const baseStyles = `
    group relative
    inline-flex min-h-[44px]
    max-w-full
    items-center justify-center
    gap-2.5
    whitespace-nowrap

    rounded-xl
    border
    px-5 py-2.5

    font-kufi
    text-sm font-semibold
    leading-6

    shadow-soft
    transition-all
    duration-300
    ease-out

    focus-visible:outline-none
    focus-visible:ring-2
    focus-visible:ring-skin-accent
    focus-visible:ring-offset-2
    focus-visible:ring-offset-skin-page

    group-focus-visible:ring-2
    group-focus-visible:ring-skin-accent
    group-focus-visible:ring-offset-2
  `;

  const stateStyles = disabled
    ? `
      cursor-not-allowed
      opacity-45
      shadow-none
      hover:translate-y-0
    `
    : `
      cursor-[inherit]
      hover:-translate-y-0.5
      hover:shadow-card
      active:translate-y-0
      active:scale-[0.98]
    `;

  const buttonClasses = `
    ${baseStyles}
    ${variants[variant] || variants.primary}
    ${stateStyles}
    ${className}
  `;

  const content = (
    <>
      {/* Button label */}
      {text && (
        <span className="relative z-10">
          {text}
        </span>
      )}

      {/* Optional icon */}
      {icon && (
        <span
          className="
            relative z-10
            inline-flex
            shrink-0
            items-center justify-center

            [&>svg]:h-[19px]
            [&>svg]:w-[19px]

            transition-transform
            duration-300
         directional-arrow
            group-hover:scale-105
          "
          aria-hidden="true"
        >
          {icon}
        </span>
      )}
    </>
  );

  /* ======================================
     BUTTON WITH ACTION
  ====================================== */

  if (hasAction) {
    return (
      <button
        type={type}
        disabled={disabled}
        onClick={onClick}
        className={buttonClasses}
      >
        {content}
      </button>
    );
  }

  /* ======================================
     PRESENTATIONAL BUTTON

     Safe to wrap inside React Router Link.
     No click handler or internal action.
  ====================================== */

  return (
    <span
      className={buttonClasses}
      aria-disabled={disabled || undefined}
    >
      {content}
    </span>
  );
}

export default ActionButton;
